import type { StarterEntry } from "../../ops";
import {
  parseEmailTemplatesListResponse,
  extractMergeTags,
  stripHtml,
} from "../marketing/marketingTemplateUtils";

/** Keys from audit 04C §B + production DB aliases (433/434). */
const TRANSACTIONAL_TEMPLATE_KEYS = new Set([
  "lifescribe_email_verification",
  "lifescribe_email_verified",
  "lifescribe_password_reset_otp",
  "lifescribe_password_reset_success",
  "lifescribe_password_changed",
  "lifescribe_account_security_alert",
  "lifescribe_password_reset_lockout",
  "lifescribe_new_device_login_alert",
  "lifescribe_email_change_alert_old",
  "lifescribe_email_change_verification_new",
  "recovery_email_verification",
  "recovery_email_verified",
  "recovery_email_added_notice",
  "recovery_email_removed_notice",
  "recovery_login_initiated_alert",
  "lifescribe_welcome",
  "lifescribe_session_expired",
  "template_password_reset_requested",
  "template_password_reset_success",
  "template_user_signup",
  "subscription_activated",
  "subscription_renewed",
  "subscription_cancelled",
  "lifescribe_subscription_activated",
  "lifescribe_subscription_renewed",
  "lifescribe_subscription_cancelled",
  "template_subscription_activated",
  "template_subscription_renewed",
  "template_subscription_cancelled",
  "lifescribe_subscription_expiring",
  "lifescribe_trial_ending_3days",
  "lifescribe_trial_ending_3day",
  "lifescribe_payment_failed",
  "lifescribe_payment_failed_final",
  "payment_failed_1",
  "payment_failed_final",
  "vault_member_invitation",
  "vault_member_welcome",
  "vault_member_accepted",
  "vault_member_declined",
  "vault_member_invitation_accepted",
  "vault_member_invitation_declined",
  "vault_member_removed",
  "vault_role_changed",
  "vault_member_role_changed",
  "vault_subscription_welcome",
  "vault_subscription_upgraded",
  "vault_subscription_downgraded",
  "vault_subscription_cancelled",
  "vault_subscription_reactivated",
  "vault_payment_success",
  "vault_payment_failed",
  "vault_subscription_expiring",
  "vault_invoice_available",
  "vault_trial_started",
  "vault_trial_ending",
  "vault_storage_warning",
  "vault_storage_critical",
  "vault_share_link_created",
  "vault_file_retrieval_ready",
  "lifescribe_family_invitation_sent_confirmation",
  "lifescribe_family_invitation_reminder_day3",
  "lifescribe_family_invitation_reminder_day6",
  "lifescribe_family_invitation_expired_notify_inviter",
  "lifescribe_family_member_removed",
  "family_invitation",
  "lifescribe_family_invitation",
  "template_family_invitation",
  "gift_purchase_confirmation",
  "gift_notification",
  "gift_redemption_confirmation",
  "gift_redeemed_notice",
  "gift_revoked_notice",
  "gift_expired_recipient",
  "gift_expired_purchaser",
  "gift_delivery_failed",
  "gift_payment_failed",
  "lifescribe_milestone_achieved",
  "lifescribe_account_deleted",
  "lifescribe_account_deletion_confirmation",
  "lifescribe_data_export_ready",
  "admin_invitation",
  "audio_deletion_warning_30_day",
  "audio_deletion_warning_15_day",
  "audio_deletion_warning_2_day",
  "audio_deletion_warning_1_day",
]);

/** Nurture templates that incorrectly use category=transactional in legacy seeds. */
const NURTURE_CATEGORY_EXCEPTIONS = new Set([
  "payment_failed_2",
  "payment_failed_3",
  "lifescribe_payment_failed_step_2",
]);

export function isTransactionalTemplate(row: {
  kind?: string | null;
  template_key?: string;
  category?: string;
}): boolean {
  if (row.template_key && NURTURE_CATEGORY_EXCEPTIONS.has(row.template_key)) {
    return false;
  }
  if (row.kind === "transactional") return true;
  if (row.kind === "nurture") return false;
  if (row.template_key && TRANSACTIONAL_TEMPLATE_KEYS.has(row.template_key)) {
    return true;
  }
  return false;
}

export function parseEmailTriggersListResponse(res: {
  data?: unknown;
  triggers?: unknown;
}): Record<string, unknown>[] {
  const d = res?.data;
  if (Array.isArray(d)) return d as Record<string, unknown>[];
  if (
    d &&
    typeof d === "object" &&
    Array.isArray((d as { triggers?: unknown }).triggers)
  ) {
    return (d as { triggers: Record<string, unknown>[] }).triggers;
  }
  if (Array.isArray(res?.triggers)) {
    return res.triggers as Record<string, unknown>[];
  }
  return [];
}

export function isTransactionalTrigger(row: {
  kind?: string | null;
  template_key?: string;
  template_category?: string;
}): boolean {
  if (row.kind === "transactional") return true;
  if (row.kind === "nurture") return false;
  if (row.template_key && TRANSACTIONAL_TEMPLATE_KEYS.has(row.template_key)) {
    return true;
  }
  return false;
}

export { parseEmailTemplatesListResponse };

// ─── Editor form helpers (parity with marketingTemplateUtils) ─────────────────
// Transactional templates store the raw DB category value directly (the
// email_templates.valid_category CHECK allows these), so the editor emits them
// as-is — no UI↔API remap needed. `kind` is always 'transactional'.

/** Categories allowed by email_templates.valid_category, transactional-relevant. */
export const TRANSACTIONAL_TEMPLATE_CATEGORIES = [
  { id: "authentication", label: "Auth & Security" },
  { id: "subscription", label: "Subscription" },
  { id: "collaboration", label: "Family Vault / Hub" },
  { id: "administrative", label: "Gift Subs / Admin" },
  { id: "engagement", label: "Engagement" },
  { id: "notification", label: "Notification" },
  { id: "transactional", label: "Transactional (generic)" },
] as const;

export interface TransactionalTemplateForm {
  template_key: string;
  template_name: string;
  description: string;
  category: string;
  subject_line: string;
  html_content: string;
  plain_text_content: string;
  preview_text: string;
  from_name: string;
  from_email: string;
  reply_to_email: string;
  status: "draft" | "active" | "inactive";
  is_active: boolean;
  /** Carried from the DB row so the editor can surface system-required merge vars. */
  required_variables: string[];
}

export const EMPTY_TRANSACTIONAL_TEMPLATE_FORM: TransactionalTemplateForm = {
  template_key: "",
  template_name: "",
  description: "",
  category: "transactional",
  subject_line: "",
  html_content: "",
  plain_text_content: "",
  preview_text: "",
  from_name: "LifeScribe",
  from_email: "no-reply@trylifescribe.com",
  reply_to_email: "support@trylifescribe.com",
  status: "active",
  is_active: true,
  required_variables: [],
};

export const DEFAULT_TRANSACTIONAL_HTML = `<!DOCTYPE html>
<html lang="en">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #1B1F23; max-width: 600px; margin: 0 auto; padding: 24px;">
  <p>Hi {{user_name}},</p>
  <p>Write your operational message here. Keep any required action links/codes.</p>
</body>
</html>`;

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((v) => String(v));
    } catch {
      /* not JSON — ignore */
    }
  }
  return [];
}

export function transactionalFormFromApiRow(
  row: Record<string, unknown>,
): TransactionalTemplateForm {
  return {
    template_key: String(row.template_key ?? ""),
    template_name: String(row.template_name ?? ""),
    description: String(row.description ?? ""),
    category: String(row.category ?? "transactional"),
    subject_line: String(row.subject_line ?? ""),
    html_content: String(row.html_content ?? ""),
    plain_text_content: String(row.plain_text_content ?? ""),
    preview_text: String(row.preview_text ?? ""),
    from_name: String(row.from_name ?? "LifeScribe"),
    from_email: String(row.from_email ?? "no-reply@trylifescribe.com"),
    reply_to_email: String(row.reply_to_email ?? "support@trylifescribe.com"),
    status: (row.status as TransactionalTemplateForm["status"]) || "active",
    is_active: row.is_active == null ? true : Boolean(row.is_active),
    required_variables: asStringArray(row.required_variables),
  };
}

export function transactionalFormFromStarter(
  starter: StarterEntry,
): TransactionalTemplateForm {
  const subject =
    (starter.subjectScaffold as string) || (starter.subjectHint as string) || "";
  const preview =
    (starter.previewTextScaffold as string) ||
    (starter.previewText as string) ||
    "";
  const category = (starter.category as string) || "transactional";
  const html = (starter.html_content as string) || DEFAULT_TRANSACTIONAL_HTML;
  return {
    ...EMPTY_TRANSACTIONAL_TEMPLATE_FORM,
    template_key: String(starter.id || "custom_transactional"),
    template_name: String(starter.name || "New Transactional Template"),
    description: String(starter.useCase || starter.description || ""),
    category,
    subject_line: subject,
    preview_text: preview,
    html_content: html,
    plain_text_content:
      (starter.plain_text_content as string) || preview || stripHtml(html),
    required_variables: asStringArray(
      (starter as Record<string, unknown>).required_variables,
    ),
  };
}

export function transactionalToApiPayload(form: TransactionalTemplateForm) {
  const tags = extractMergeTags(
    form.subject_line,
    form.html_content,
    form.plain_text_content,
    form.preview_text,
  );
  const sample: Record<string, string> = {};
  for (const t of tags) {
    sample[t] = t === "user_name" ? "Sample User" : `[${t}]`;
  }
  return {
    template_key: form.template_key.trim(),
    template_name: form.template_name.trim(),
    description: form.description.trim() || form.template_name.trim(),
    category: form.category, // already a valid DB value
    kind: "transactional",
    subject_line: form.subject_line.trim(),
    html_content: form.html_content,
    plain_text_content: form.plain_text_content || stripHtml(form.html_content),
    preview_text: form.preview_text,
    required_variables: tags,
    optional_variables: [] as string[],
    sample_data: sample,
    from_name: form.from_name.trim(),
    from_email: form.from_email.trim(),
    reply_to_email: form.reply_to_email.trim() || form.from_email.trim(),
    status: form.status,
    is_active: form.is_active,
  };
}
